type CustomElementConstructor = typeof HTMLElement

export interface LazyComponent<T extends CustomElementConstructor = CustomElementConstructor> {
	(): Promise<{ default: T }>
	preload(): Promise<void>
}

// Per-component load cache. Kept off the public LazyComponent surface so the
// interface stays a clean callable + preload() contract.
const promiseCache = new WeakMap<object, Promise<{ default: CustomElementConstructor }>>()
const moduleCache = new WeakMap<object, { default: CustomElementConstructor }>()

export function lazy<T extends CustomElementConstructor>(loader: () => Promise<{ default: T }>): LazyComponent<T> {
	const lazyComponent = function (): Promise<{ default: T }> {
		const cachedPromise = promiseCache.get(lazyComponent)
		if (cachedPromise) {
			return cachedPromise as Promise<{ default: T }>
		}

		const cachedModule = moduleCache.get(lazyComponent)
		if (cachedModule) {
			return Promise.resolve(cachedModule as { default: T })
		}

		const promise = loader()
			.then(module => {
				moduleCache.set(lazyComponent, module)
				return module
			})
			.catch(error => {
				// Clear the cached promise so a failed load can be retried, not permanently stuck.
				promiseCache.delete(lazyComponent)
				throw error
			})
		promiseCache.set(lazyComponent, promise)

		return promise
	} as LazyComponent<T>

	lazyComponent.preload = async function (): Promise<void> {
		try {
			await lazyComponent()
		} catch (error) {
			console.error('Failed to preload component:', error)
		}
	}

	return lazyComponent
}
